/**
 * History (docs/history-ux.md) — the settled P&L archive, in the shared "soft cards" look
 * (docs/markets-ux.md §0):
 *
 *   top       Real / Paper (`?book=paper` opens Paper — the Desk's summary card links here) and
 *             the range pills 7d · 30d · 90d · YTD · All, which drive everything except the
 *             calendar's month; stat cards Net P&L · ROI · Win rate · Settled · Staked · Pending
 *   main      the cumulative P&L chart (`performance?group_by=day`, sorted + gap-filled), then the
 *             settled-bets list (result filtered on the server; venue, league and search on the
 *             loaded rows, which the UI says) with "Load more" and the resolution detail per row
 *   rail      the compact heat calendar (its own month stepper; a day filters the list and
 *             highlights the chart), then By venue / By league / By model breakdowns
 *   plays     Revision: the list's Bets / Plays toggle folds an opportunity's settled legs (2+)
 *             into one row with the net P&L (lib/plays.ts). In Plays the result filter runs on
 *             the loaded rows (a play's result is its net), so the server's `result=` is dropped.
 *
 * Mobile stacks: sticky pills, 2-column stats, chart, calendar, list, collapsible breakdowns.
 * Read-only: nothing here places, confirms or cancels anything.
 *
 * The screen reads everything through a `HistorySource` so the dev preview (/dev/history) can
 * render it from mock data; `History` below wires the live hooks.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import type { Contest, League, Order, Performance, PerformanceCurrency } from "@greenbook/client";
import { usePerformance as usePerformanceQuery, useOrders } from "../lib/queries.js";
import { usePartnerLogos } from "../lib/logos.js";
import { useIsMobile } from "../lib/responsive.js";
import { fmtRoi } from "../lib/format.js";
import { ErrorState, Skeleton } from "../components/ui.js";
import { VenueLogo } from "../components/VenueLogo.js";
import type { ResolutionState } from "../components/ResolutionDetail.js";
import { PnlChart } from "../components/history/PnlChart.js";
import { MiniCalendar } from "../components/history/MiniCalendar.js";
import { Breakdown } from "../components/history/Breakdown.js";
import { EmptyHistory, gameSearchText, SettledList, type ListView } from "../components/history/SettledList.js";
import { itemResult, settledItems } from "../lib/plays.js";
import { useMmQuoteIds } from "../lib/mmModel.js";
import { liveSource as portfolioSource, type ListState } from "./Portfolio.js";
import {
  addDays,
  ALL_FROM,
  daySeries,
  DEFAULT_RANGE,
  filterSettled,
  fmtAmt,
  fmtSignedAmt,
  hasClientFilter,
  isoDay,
  modelBreakdownUseful,
  monthWindow,
  RANGES,
  rangePhrase,
  rangeWindow,
  settledBets,
  winRate,
  type Book,
  type GroupLike,
  type Range,
  type ResultFilter,
} from "../lib/history.js";

/* =================================================================== source */

export type GroupBy = "none" | "day" | "partner" | "league" | "model";

export interface QueryState<T> {
  data?: T;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

/**
 * Everything the screen reads. Each `use*` is a hook, called unconditionally in the same order
 * on every render; the object itself must be stable for the screen's lifetime.
 */
export interface HistorySource {
  /** "Now" (fixed in dev previews so the mock calendar lines up). */
  now(): number;
  usePerformance(groupBy: GroupBy, from: string, to: string, enabled?: boolean): QueryState<Performance>;
  /** Settled orders in `[from, to)` by settlement time, newest first; `result` filters on the server. */
  useSettled(q: { ccy: Book; from: string; to: string; result: ResultFilter | null }): ListState<Order>;
  useContests(ids: string[]): (id: string) => Contest | undefined;
  useLeagues(): (id: string | undefined) => League | undefined;
  useResolution(id: string, enabled: boolean): ResolutionState;
  useLogos(): Map<string, string>;
  /** Opportunity ids greenbook's Market making screen created (labels those plays). */
  useMmIds(): ReadonlySet<string>;
}

/** Rows per settled-orders page. */
const PAGE = 100;
/** With a client-side filter on and few matches, keep paging up to this many loaded rows. */
const AUTO_LOAD_ROWS = 500;
const AUTO_LOAD_MIN_MATCHES = 20;

export const liveSource: HistorySource = {
  now: () => Date.now(),
  usePerformance(groupBy, from, to, enabled = true) {
    const q = usePerformanceQuery({ groupBy, from, to, enabled });
    return { data: q.data, isLoading: q.isLoading, isError: q.isError, error: q.error, refetch: () => void q.refetch() };
  },
  useSettled({ ccy, from, to, result }) {
    const q = useOrders(PAGE, { settled: true, sort: "settled", currency: ccy, from, to, result: result ?? undefined });
    const items = useMemo(() => (q.data?.pages ?? []).flatMap((p) => p.data), [q.data]);
    return {
      items,
      isLoading: q.isLoading,
      isError: q.isError,
      error: q.error,
      refetch: () => void q.refetch(),
      hasMore: !!q.hasNextPage,
      loadingMore: q.isFetchingNextPage,
      loadMore: () => void q.fetchNextPage(),
    };
  },
  // The contest meta lookup Portfolio uses (one cached `/contests/:id` per game).
  useContests: portfolioSource.useContests,
  useLeagues: portfolioSource.useLeagues,
  useResolution: portfolioSource.useResolution,
  useLogos: usePartnerLogos,
  useMmIds: useMmQuoteIds,
};

/** The routed page (`/history`). */
export function History() {
  const [params] = useSearchParams();
  return <HistoryScreen source={liveSource} initialBook={params.get("book") === "paper" ? "ATLAS" : "USD"} />;
}

/* =================================================================== helpers */

/** Pick the real-money (USD) or play-money (ATLAS) bucket from a performance payload. */
export function pickCurrency(perf: Performance | undefined, real: boolean): PerformanceCurrency | undefined {
  return perf?.by_currency?.find((c) => (c.funding?.is_real_money ?? false) === real);
}

const EMPTY: GroupLike[] = [];

/* =================================================================== screen */

export function HistoryScreen({
  source,
  initialBook = "USD",
  initialRange = DEFAULT_RANGE,
  initialDay = null,
  initialFilters,
  initialView = "bets",
}: {
  source: HistorySource;
  initialBook?: Book;
  initialRange?: Range;
  initialDay?: string | null;
  initialFilters?: { result?: ResultFilter | null; venue?: string | null; league?: string | null; q?: string };
  initialView?: ListView;
}) {
  const isMobile = useIsMobile();
  const logos = source.useLogos();
  const now = source.now();
  const today = isoDay(now);

  const [book, setBook] = useState<Book>(initialBook);
  const real = book === "USD";
  const [range, setRange] = useState<Range>(initialRange);
  const [month, setMonth] = useState<string>(() => monthWindow(initialDay ?? today).from);
  const [day, setDay] = useState<string | null>(initialDay);
  const [result, setResult] = useState<ResultFilter | null>(initialFilters?.result ?? null);
  const [venue, setVenue] = useState<string | null>(initialFilters?.venue ?? null);
  const [league, setLeague] = useState<string | null>(initialFilters?.league ?? null);
  const [q, setQ] = useState(initialFilters?.q ?? "");
  const [view, setView] = useState<ListView>(initialView);
  const plays = view === "plays";
  const mmIds = source.useMmIds();

  // ------------------------------------------------------------- performance
  const win = useMemo(() => rangeWindow(range, now), [range, today]); // eslint-disable-line react-hooks/exhaustive-deps
  const perfNone = source.usePerformance("none", win.from, win.to);
  const perfDay = source.usePerformance("day", win.from, win.to);
  const perfVenue = source.usePerformance("partner", win.from, win.to);
  const perfLeague = source.usePerformance("league", win.from, win.to);
  const perfModel = source.usePerformance("model", win.from, win.to);
  const mWin = monthWindow(month);
  const perfMonth = source.usePerformance("day", mWin.from, mWin.to);
  const perfSelDay = source.usePerformance("none", day ?? today, addDays(day ?? today, 1), !!day);

  const kpi = pickCurrency(perfNone.data, real);
  const rangeBets = kpi ? settledBets(kpi) : 0;
  const rangeEmpty = !perfNone.isLoading && !perfNone.isError && rangeBets === 0;
  const perfAll = source.usePerformance("none", ALL_FROM, win.to, rangeEmpty && range !== "all");
  const allBets = settledBets(pickCurrency(perfAll.data, real) ?? {});

  const dayGroups = pickCurrency(perfDay.data, real)?.groups ?? EMPTY;
  const series = useMemo(() => daySeries(dayGroups, win, { trimToData: range === "all" }), [dayGroups, win, range]);
  const venueGroups = pickCurrency(perfVenue.data, real)?.groups ?? EMPTY;
  const leagueGroups = pickCurrency(perfLeague.data, real)?.groups ?? EMPTY;
  const modelGroups = pickCurrency(perfModel.data, real)?.groups ?? EMPTY;
  const monthCur = pickCurrency(perfMonth.data, real);
  const selDayCur = pickCurrency(perfSelDay.data, real);

  // ------------------------------------------------------------------- list
  const listWin = day ? { from: day, to: addDays(day, 1) } : win;
  // Plays: a play's result is its net, so every leg loads and the result filter runs here.
  const list = source.useSettled({ ccy: book, from: listWin.from, to: listWin.to, result: plays ? null : result });
  const leagueOf = source.useLeagues();
  const filters = { result: plays ? null : result, venue, league, q };
  const clientFiltering = hasClientFilter(filters) || (plays && !!result);

  // Contest meta: every loaded row's game while a league filter or search needs it; otherwise
  // just enough for the rows on screen.
  const needAllContests = !!league || !!q.trim();
  const preFiltered = useMemo(() => filterSettled(list.items, { result: filters.result, venue }, () => undefined), [list.items, filters.result, venue]);
  const contestIds = useMemo(() => {
    const src = needAllContests ? list.items : preFiltered;
    return [...new Set(src.map((o) => o.contest_id).filter((x): x is string => !!x))];
  }, [needAllContests, list.items, preFiltered]);
  const contestOf = source.useContests(contestIds);
  const leagueIdOf = useCallback((cid: string) => contestOf(cid)?.league_id ?? undefined, [contestOf]);
  const rows = useMemo(
    () => filterSettled(list.items, filters, leagueIdOf, (cid) => gameSearchText(contestOf(cid))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list.items, filters.result, venue, league, q, leagueIdOf, contestOf],
  );
  // The Plays view: legs sharing an opportunity folded into one row, then the result filter.
  const items = useMemo(() => {
    if (!plays) return [];
    const all = settledItems(rows, { mmIds });
    return result ? all.filter((it) => itemResult(it) === result) : all;
  }, [plays, rows, mmIds, result]);
  const listCount = plays ? items.length : rows.length;
  const leagueName = useCallback(
    (cid: string | null | undefined) => {
      if (!cid) return null;
      const lid = contestOf(cid)?.league_id;
      return (lid && (leagueOf(lid)?.name ?? leagueGroups.find((g) => g.key === lid)?.label)) || null;
    },
    [contestOf, leagueOf, leagueGroups],
  );

  // A client-side filter with few matches pages on by itself (up to AUTO_LOAD_ROWS loaded rows).
  const { hasMore, loadingMore, loadMore, isLoading: listLoading } = list;
  useEffect(() => {
    if (clientFiltering && hasMore && !loadingMore && !listLoading && listCount < AUTO_LOAD_MIN_MATCHES && list.items.length < AUTO_LOAD_ROWS) loadMore?.();
  }, [clientFiltering, hasMore, loadingMore, listLoading, listCount, list.items.length, loadMore]);

  // ------------------------------------------------------------------ actions
  const pickBook = (b: Book) => {
    setBook(b);
    setVenue(null); // venues differ per book (paper fills land on the OpenMarkets book)
  };
  const clearAll = () => {
    setResult(null);
    setVenue(null);
    setLeague(null);
    setQ("");
    setDay(null);
  };
  const selectDay = (d: string | null) => setDay(d);

  // ---------------------------------------------------------------- pieces
  const bookWord = real ? "real-money" : "paper";
  const phrase = rangePhrase(range);
  const emptyRangeText = range === "all" ? `No settled ${bookWord} bets yet` : `No settled ${bookWord} bets ${phrase}`;

  const controls = (
    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
      <Segmented
        label="Book"
        value={book}
        onChange={(v) => pickBook(v as Book)}
        options={[
          { id: "USD", label: isMobile ? "Real" : "Real · USD", title: "real money" },
          { id: "ATLAS", label: isMobile ? "Paper" : "Paper · ATLAS", title: "play money" },
        ]}
      />
      <Segmented label="Range" value={range} onChange={(v) => setRange(v as Range)} options={RANGES.map((r) => ({ id: r.id, label: r.label }))} />
    </div>
  );

  const winPct = kpi ? winRate(kpi) : null;
  const stats = perfNone.isError ? (
    <ErrorState error={perfNone.error} onRetry={perfNone.refetch} />
  ) : (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6 lg:gap-3" aria-busy={perfNone.isLoading || undefined}>
      <Stat loading={perfNone.isLoading} label="Net P&L" value={<Signed n={kpi?.net_pl ?? 0} ccy={book} />} sub={range === "all" ? "all time" : phrase.replace(/^in the /, "")} />
      <Stat loading={perfNone.isLoading} label="ROI" value={<span className={toneOf(kpi?.roi ?? 0)}>{fmtRoi(kpi?.roi)}</span>} sub="net ÷ staked" />
      <Stat
        loading={perfNone.isLoading}
        label="Win rate"
        value={winPct == null ? "—" : `${Math.round(winPct * 100)}%`}
        sub={`${kpi?.wins ?? 0}W · ${kpi?.losses ?? 0}L · ${kpi?.pushes ?? 0}P`}
      />
      <Stat
        loading={perfNone.isLoading}
        label="Settled"
        value={rangeBets.toLocaleString()}
        sub={(kpi?.cancelled ?? 0) > 0 ? `bets · ${kpi?.cancelled} cancelled` : "bets"}
      />
      <Stat loading={perfNone.isLoading} label="Staked" value={fmtAmt(kpi?.staked ?? 0, book)} sub="turnover" />
      <Stat loading={perfNone.isLoading} label="Pending" value={(kpi?.pending_count ?? 0).toLocaleString()} sub="open now" />
    </div>
  );

  const chart = (
    <section aria-label="Cumulative P&L" className="rounded-lg border border-border bg-panel-2 p-3 lg:p-4">
      <div className="mb-2 flex items-baseline gap-2">
        <h2 className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-wider text-dim">Cumulative P&L</h2>
        <span className="hidden text-[11px] text-faint sm:inline">line: running total · bars: each day</span>
      </div>
      {perfDay.isLoading ? (
        <div style={{ height: isMobile ? 180 : 220 }}>
          <Skeleton className="h-full w-full rounded-md" />
        </div>
      ) : perfDay.isError ? (
        <ErrorState error={perfDay.error} onRetry={perfDay.refetch} />
      ) : (
        <PnlChart series={series} ccy={book} selectedDay={day} height={isMobile ? 180 : 220} />
      )}
    </section>
  );

  const calendar = (
    <section aria-label="Calendar" className="rounded-lg border border-border bg-panel-2 p-3">
      <MiniCalendar
        month={month}
        onMonthChange={setMonth}
        groups={monthCur?.groups ?? EMPTY}
        loading={perfMonth.isLoading}
        error={perfMonth.isError}
        ccy={book}
        today={today}
        selectedDay={day}
        onSelectDay={selectDay}
        monthNet={monthCur ? (monthCur.net_pl ?? 0) : null}
      />
    </section>
  );

  const venueOptions = useMemo(
    () => [...venueGroups].sort((a, b) => (b.settled_count ?? 0) - (a.settled_count ?? 0)).map((g) => ({ key: g.key, label: g.label ?? g.key })),
    [venueGroups],
  );
  const leagueOptions = useMemo(
    () => [...leagueGroups].sort((a, b) => (b.settled_count ?? 0) - (a.settled_count ?? 0)).map((g) => ({ key: g.key, label: g.label ?? g.key })),
    [leagueGroups],
  );
  const venueLogoFor = (g: GroupLike) => <VenueLogo name={g.label ?? g.key} logoUrl={logos.get(g.key)} size={16} />;

  const breakdowns = (
    <>
      <Breakdown
        title="By venue"
        groups={venueGroups}
        ccy={book}
        loading={perfVenue.isLoading}
        error={perfVenue.isError ? perfVenue.error : undefined}
        onRetry={perfVenue.refetch}
        collapsible={isMobile}
        activeKey={venue}
        onPick={(k) => setVenue((v) => (v === k ? null : k))}
        iconFor={venueLogoFor}
        pickHint="Tap a venue to filter the bets."
      />
      <Breakdown
        title="By league"
        groups={leagueGroups}
        ccy={book}
        loading={perfLeague.isLoading}
        error={perfLeague.isError ? perfLeague.error : undefined}
        onRetry={perfLeague.refetch}
        collapsible={isMobile}
        activeKey={league}
        onPick={(k) => setLeague((v) => (v === k ? null : k))}
      />
      {!perfModel.isLoading && !perfModel.isError && modelBreakdownUseful(modelGroups) && (
        <Breakdown title="By model" groups={modelGroups} ccy={book} loading={false} collapsible={isMobile} />
      )}
    </>
  );

  const dayGroup: GroupLike | null = day && selDayCur ? { key: day, ...selDayCur } : null;
  const listEmptyText = day
    ? `No settled ${bookWord} bets on this day${clientFiltering || result ? " match these filters" : ""}.`
    : clientFiltering || result
      ? `No loaded bets match these filters.`
      : `No settled ${bookWord} bets ${phrase || "yet"}.`;

  const settled = (
    <SettledList
      ccy={book}
      rows={rows}
      loading={list.isLoading}
      isError={list.isError}
      error={list.error}
      onRetry={list.refetch}
      hasMore={!!list.hasMore}
      loadingMore={!!list.loadingMore}
      onLoadMore={() => list.loadMore?.()}
      loadedCount={list.items.length}
      clientFiltering={clientFiltering}
      result={result}
      onResult={setResult}
      venue={venue}
      onVenue={setVenue}
      venues={venueOptions}
      league={league}
      onLeague={setLeague}
      leagues={leagueOptions}
      q={q}
      onQ={setQ}
      day={day ? { day, group: dayGroup, loading: perfSelDay.isLoading } : null}
      onClearDay={() => setDay(null)}
      onClearAll={clearAll}
      contestOf={contestOf}
      leagueName={leagueName}
      logos={logos}
      useResolution={source.useResolution}
      emptyText={listEmptyText}
      view={view}
      onView={setView}
      items={items}
    />
  );

  const showEmpty = rangeEmpty && !day;
  const empty = (
    <EmptyHistory
      text={emptyRangeText}
      onShowAll={range !== "all" && allBets > 0 ? () => setRange("all") : undefined}
    />
  );

  if (isMobile) {
    return (
      <div className="flex flex-col gap-3 px-4 pb-6">
        <div className="sticky top-0 z-20 -mx-4 border-b border-border bg-bg/95 px-4 py-2 backdrop-blur">{controls}</div>
        {showEmpty ? (
          empty
        ) : (
          <>
            {stats}
            {chart}
            {calendar}
            {settled}
            {breakdowns}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4 xl:p-5">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="mr-1 text-[11px] font-semibold uppercase tracking-wider text-dim">History</h1>
        {controls}
      </div>
      {showEmpty ? (
        empty
      ) : (
        <>
          {stats}
          <div className="grid grid-cols-[minmax(0,1fr)_320px] items-start gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
            <div className="flex min-w-0 flex-col gap-4">
              {chart}
              {settled}
            </div>
            <aside className="flex min-w-0 flex-col gap-3" aria-label="Calendar and breakdowns">
              {calendar}
              {breakdowns}
            </aside>
          </div>
        </>
      )}
    </div>
  );
}

/* ================================================================== bits */

const toneOf = (n: number): string => (n >= 0.005 ? "text-up" : n <= -0.005 ? "text-down" : "text-dim");

function Signed({ n, ccy }: { n: number; ccy: Book }) {
  return <span className={toneOf(n)}>{fmtSignedAmt(n, ccy)}</span>;
}

function Stat({ label, value, sub, loading }: { label: string; value: ReactNode; sub?: ReactNode; loading?: boolean }) {
  return (
    <div className="min-w-0 rounded-lg border border-border bg-panel-2 p-3 lg:p-4">
      <div className="text-[11px] uppercase tracking-wide text-faint">{label}</div>
      {loading ? (
        <>
          <Skeleton className="mt-1.5 h-6 w-20 max-w-full" />
          <Skeleton className="mt-1.5 h-3 w-16" />
        </>
      ) : (
        <>
          <div className="nums mt-1 break-words text-lg font-semibold leading-tight text-ink lg:text-xl">{value}</div>
          {sub && <div className="mt-1 truncate text-[11px] text-dim">{sub}</div>}
        </>
      )}
    </div>
  );
}

/** A rounded-full group of pills, amber when active; 44 px targets on touch. */
function Segmented({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { id: string; label: string; title?: string }[];
  onChange: (id: string) => void;
}) {
  return (
    <div className="flex items-center rounded-full border border-border-2 bg-panel-2 p-0.5 lg:gap-0.5" role="group" aria-label={label}>
      {options.map((o) => {
        const active = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            aria-pressed={active}
            title={o.title}
            className={`flex min-h-11 min-w-11 items-center justify-center rounded-full px-1.5 text-[13px] sm:px-2.5 font-medium transition-colors duration-150 motion-reduce:transition-none lg:min-h-7 lg:px-3 ${
              active ? "bg-amber/15 text-amber ring-1 ring-amber/60" : "text-dim hover:bg-raised hover:text-ink"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
